import { useState, useMemo, ReactNode } from "react";
import {
  ChevronDown, ChevronRight, Eye, EyeOff, CheckSquare, Square,
  BookOpen, Menu, X, ArrowUp, RotateCcw, Shuffle,
} from "lucide-react";
import LU5PseudocodeExercises from "./LU5PseudocodeExercises";
import LU5FlowchartExercises from "./LU5FlowchartExercises";

// ─── Types ────────────────────────────────────────────────────────────────────

interface FlashCard {
  cat: string;
  front: string;
  back: string;
}

interface SectionProps {
  id: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  completed: boolean;
  onToggleComplete: () => void;
  defaultOpen?: boolean;
}

interface InfoBoxProps {
  variant?: "info" | "warn" | "tip" | "key";
  title?: string;
  children: ReactNode;
}

interface TableProps {
  headers: string[];
  rows: ReactNode[][];
}

interface WorkedProps {
  title?: string;
  children: ReactNode;
}

// ─── Flashcard Data ───────────────────────────────────────────────────────────

const FLASHCARDS: FlashCard[] = [
  { cat: "Algorithms", front: "What is an algorithm?", back: "A step-by-step set of instructions to solve a problem or complete a task. It must be finite, unambiguous, and produce the correct output." },
  { cat: "Algorithms", front: "What are the four steps of problem-solving?", back: "1) Understand the problem (inputs, outputs, constraints). 2) Plan a solution (algorithm/pseudocode/flowchart). 3) Code the solution. 4) Test and debug." },
  { cat: "Algorithms", front: "What are the three basic control structures?", back: "Sequence (instructions executed one after the other), Selection (IF/ELSE decisions), and Iteration (repeating instructions using loops)." },
  { cat: "Algorithms", front: "What is IPO?", back: "Input → Processing → Output. The basic model of how every program works: take data in, do something with it, produce a result." },
  { cat: "Algorithms", front: "What does 'finite' mean for an algorithm?", back: "An algorithm must end — it cannot run forever. Every algorithm must have a clear stopping point." },
  { cat: "Pseudocode", front: "What is pseudocode?", back: "A structured, language-independent way of describing an algorithm using plain English words and programming-like structure. Not real code — cannot be executed." },
  { cat: "Pseudocode", front: "What keywords mark the start and end of pseudocode?", back: "BEGIN and END." },
  { cat: "Pseudocode", front: "Write pseudocode to get a user's name and greet them.", back: 'BEGIN\n  DISPLAY "What is your name?"\n  INPUT name\n  DISPLAY "Hello, " + name\nEND' },
  { cat: "Pseudocode", front: "What keywords are used for selection (decisions)?", back: "IF ... THEN ... ELSE IF ... ELSE ... END IF" },
  { cat: "Pseudocode", front: "What keywords are used for iteration (loops)?", back: "FOR ... NEXT (counted loop), WHILE ... END WHILE (conditional loop), REPEAT ... UNTIL (post-test loop)." },
  { cat: "Pseudocode", front: "What does INPUT do in pseudocode?", back: "It pauses the program to receive data from the user and store it in a variable." },
  { cat: "Pseudocode", front: "What does DISPLAY (or OUTPUT) do?", back: "It shows information to the user on the screen." },
  { cat: "Flowcharts", front: "What shape represents the start or end of a flowchart?", back: "An oval (also called a terminator). Contains the word START or END." },
  { cat: "Flowcharts", front: "What shape represents input or output?", back: "A parallelogram." },
  { cat: "Flowcharts", front: "What shape represents a process (a calculation or assignment)?", back: "A rectangle." },
  { cat: "Flowcharts", front: "What shape represents a decision?", back: "A diamond (rhombus). It has one arrow in and two arrows out (labelled Yes/No or True/False)." },
  { cat: "Flowcharts", front: "How are flowchart shapes connected?", back: "With arrows (flow lines) that show the direction the program follows." },
  { cat: "Flowcharts", front: "How many arrows leave a decision diamond?", back: "Exactly two — one for TRUE/Yes and one for FALSE/No." },
  { cat: "Flowcharts", front: "How do you show a loop (iteration) in a flowchart?", back: "Use a decision diamond whose Yes branch runs the loop body, then has an arrow going back UP to the diamond again. The No branch exits the loop. The 'arrow going back up' is the visual signature of a loop." },
  { cat: "Flowcharts", front: "What does it mean to 'trace' a flowchart?", back: "To follow the flowchart step-by-step with a specific input, writing down what each step does and what gets displayed. Also called a dry run or desk check." },
  { cat: "Java", front: "What is a variable?", back: "A named storage location in memory that holds a value that can change while the program runs." },
  { cat: "Java", front: "Name the Java data types you need to know for Grade 10.", back: "int (whole numbers), double (decimal numbers), String (text), boolean (true/false), char (single character)." },
  { cat: "Java", front: "How do you declare and initialise an int variable called age with the value 15?", back: "int age = 15;" },
  { cat: "Java", front: "What is the difference between = and ==?", back: "= is the assignment operator (puts a value into a variable). == is the comparison operator (checks if two values are equal)." },
  { cat: "Java", front: "What does System.out.println do?", back: "It prints a line of output to the screen and then moves the cursor to the next line." },
  { cat: "Java", front: "What is the difference between println and print?", back: "println adds a newline at the end (the next output goes on a new line). print does not add a newline." },
  { cat: "Java", front: "Every Java statement ends with what character?", back: "A semicolon ( ; )." },
  { cat: "Java", front: 'What does String name = "Sipho"; do?', back: 'It declares a String variable called name and stores the text "Sipho" in it.' },
  { cat: "Math methods", front: "What does Math.random() return?", back: "A random double between 0.0 (inclusive) and 1.0 (exclusive)." },
  { cat: "Math methods", front: "How do you generate a random integer between 1 and 10 inclusive?", back: "int n = (int)(Math.random() * 10) + 1;" },
  { cat: "Math methods", front: "What does Math.round() do?", back: "It rounds a double to the nearest whole number and returns a long." },
  { cat: "Math methods", front: "What is casting?", back: "Converting a value from one data type to another, e.g. (int)3.7 gives 3. Written as (typename) in front of the value." },
  { cat: "Math methods", front: "Why do we cast Math.random() to int?", back: "Because Math.random() returns a double. To use it as a whole number (for counting, array indexes, dice rolls), we must cast it to int, which truncates the decimal." },
  { cat: "If statements", front: 'Write a Java if-statement to print "Pass" if mark is 50 or more.', back: 'if (mark >= 50) {\n    System.out.println("Pass");\n}' },
  { cat: "If statements", front: "What are the six comparison operators?", back: "== (equal), != (not equal), > (greater than), < (less than), >= (greater or equal), <= (less or equal)." },
  { cat: "If statements", front: "What does else if do?", back: "It checks another condition if the first if was false. You can chain multiple else if blocks." },
  { cat: "If statements", front: "What does else do?", back: "It runs if all previous if and else if conditions were false. A 'catch-all' for the remaining cases." },
  { cat: "If statements", front: "What are the logical operators?", back: "&& (AND — both true), || (OR — at least one true), ! (NOT — reverses true/false)." },
  { cat: "Loops", front: "What is a for loop used for?", back: "A counted loop — when you know in advance how many times the code must repeat." },
  { cat: "Loops", front: "Write a for loop that prints 1 to 5.", back: "for (int i = 1; i <= 5; i++) {\n    System.out.println(i);\n}" },
  { cat: "Loops", front: "What are the three parts of a for loop header?", back: "Initialisation (int i = 1), Condition (i <= 5), and Update (i++). Separated by semicolons." },
  { cat: "Loops", front: "What is a while loop used for?", back: "A conditional loop — when you don't know in advance how many times to repeat, but you know the condition that must stay true." },
  { cat: "Loops", front: "What does i++ mean?", back: "It adds 1 to i. Equivalent to i = i + 1." },
  { cat: "Loops", front: "What is an infinite loop?", back: "A loop whose condition never becomes false, so it runs forever. Usually a bug — often caused by forgetting to update the loop variable." },
  { cat: "Tracing", front: "What is tracing code?", back: "Working through code by hand, line by line, keeping track of variable values to predict the output. Also called a dry run or desk check." },
  { cat: "Tracing", front: "What is a trace table?", back: "A table used to track variable values as code executes. One column per variable, one row per change." },
];

const CATEGORIES = Array.from(new Set(FLASHCARDS.map(c => c.cat)));

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ id, title, subtitle, children, completed, onToggleComplete, defaultOpen = false }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="mb-6 rounded-2xl border border-amber-900/20 bg-gradient-to-br from-amber-50/80 to-orange-50/60 shadow-sm overflow-hidden" style={{ scrollMarginTop: "5rem" }}>
      <div className="flex items-stretch">
        <button onClick={() => setOpen(!open)} className="flex-1 flex items-center gap-3 px-5 py-4 text-left hover:bg-amber-100/40 transition">
          {open ? <ChevronDown className="w-5 h-5 text-amber-900 flex-shrink-0" /> : <ChevronRight className="w-5 h-5 text-amber-900 flex-shrink-0" />}
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl text-amber-950 font-semibold leading-tight" style={{ fontFamily: "Palatino, 'Palatino Linotype', Georgia, serif" }}>{title}</h2>
            {subtitle && <p className="text-sm text-amber-800/80 mt-1">{subtitle}</p>}
          </div>
        </button>
        <button onClick={(e) => { e.stopPropagation(); onToggleComplete(); }} className="px-4 flex items-center justify-center hover:bg-amber-100/60 transition border-l border-amber-900/10" title={completed ? "Mark as not done" : "Mark as done"}>
          {completed ? <CheckSquare className="w-5 h-5 text-emerald-700" /> : <Square className="w-5 h-5 text-amber-900/40" />}
        </button>
      </div>
      {open && <div className="px-5 pb-6 pt-1 text-amber-950/90 leading-relaxed">{children}</div>}
    </section>
  );
}

function Spoiler({ label = "Show answer", children }: { label?: string; children: ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div className="my-3">
      <button onClick={() => setShow(!show)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-900 text-amber-50 text-sm hover:bg-amber-950 transition">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        {show ? "Hide answer" : label}
      </button>
      {show && <div className="mt-2 p-4 rounded-lg bg-emerald-50 border-l-4 border-emerald-600 text-emerald-950">{children}</div>}
    </div>
  );
}

function InfoBox({ variant = "info", title, children }: InfoBoxProps) {
  const variants = {
    info:  "bg-sky-50 border-sky-500 text-sky-950",
    warn:  "bg-rose-50 border-rose-500 text-rose-950",
    tip:   "bg-amber-50 border-amber-600 text-amber-950",
    key:   "bg-indigo-50 border-indigo-500 text-indigo-950",
  };
  return (
    <div className={`my-4 p-4 rounded-lg border-l-4 ${variants[variant]}`}>
      {title && <div className="font-semibold mb-1">{title}</div>}
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Table({ headers, rows }: TableProps) {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-amber-900/20">
      <table className="w-full text-sm">
        <thead className="bg-amber-900 text-amber-50">
          <tr>{headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-amber-50/60" : "bg-white/80"}>
              {row.map((cell, j) => <td key={j} className="px-3 py-2 border-t border-amber-900/10 align-top">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Worked({ title, children }: WorkedProps) {
  return (
    <div className="my-4 p-4 rounded-lg bg-violet-50 border border-violet-300">
      <div className="text-xs uppercase tracking-wider text-violet-700 font-bold mb-2">Worked Example</div>
      {title && <div className="font-semibold text-violet-950 mb-2">{title}</div>}
      <div className="text-violet-950 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="my-3 p-3 rounded-md bg-stone-200 text-stone-900 text-sm overflow-x-auto border border-stone-400" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
      {children}
    </pre>
  );
}

function Inline({ children }: { children: ReactNode }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-stone-200 text-stone-900 text-[0.9em] border border-stone-300" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
      {children}
    </code>
  );
}

// ─── Flashcard / Quiz ─────────────────────────────────────────────────────────

function CheatSheet() {
  const [mode, setMode] = useState<"browse" | "quiz">("browse");
  const [category, setCategory] = useState("All");
  const [flipped, setFlipped] = useState<Record<number, boolean>>({});
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizOrder, setQuizOrder] = useState<number[]>(() => FLASHCARDS.map((_, i) => i));
  const [knownCount, setKnownCount] = useState(0);
  const [stillLearning, setStillLearning] = useState(0);
  const [quizFlipped, setQuizFlipped] = useState(false);

  const filtered = useMemo(
    () => category === "All" ? FLASHCARDS : FLASHCARDS.filter(c => c.cat === category),
    [category]
  );

  const shuffle = () => {
    const order = [...Array(FLASHCARDS.length).keys()].sort(() => Math.random() - 0.5);
    setQuizOrder(order); setQuizIndex(0); setKnownCount(0); setStillLearning(0); setQuizFlipped(false);
  };

  const reset = () => {
    setQuizOrder(FLASHCARDS.map((_, i) => i)); setQuizIndex(0); setKnownCount(0); setStillLearning(0); setQuizFlipped(false); setFlipped({});
  };

  const card = FLASHCARDS[quizOrder[quizIndex]];
  const total = FLASHCARDS.length;
  const progress = total > 0 ? (quizIndex / total) * 100 : 0;

  return (
    <div className="rounded-2xl border border-amber-900/20 bg-gradient-to-br from-amber-50/80 to-orange-50/60 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-xl font-semibold text-amber-950" style={{ fontFamily: "Palatino, 'Palatino Linotype', Georgia, serif" }}>
          Flashcards ({FLASHCARDS.length})
        </h3>
        <div className="flex gap-2">
          <button onClick={() => setMode("browse")} className={`px-3 py-1.5 rounded-md text-sm ${mode === "browse" ? "bg-amber-900 text-amber-50" : "bg-amber-100 text-amber-900"}`}>Browse</button>
          <button onClick={() => { setMode("quiz"); setQuizIndex(0); setKnownCount(0); setStillLearning(0); setQuizFlipped(false); }} className={`px-3 py-1.5 rounded-md text-sm ${mode === "quiz" ? "bg-amber-900 text-amber-50" : "bg-amber-100 text-amber-900"}`}>Quiz</button>
        </div>
      </div>

      {mode === "browse" && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => setCategory("All")} className={`px-2.5 py-1 rounded-md text-xs ${category === "All" ? "bg-amber-900 text-amber-50" : "bg-amber-100 text-amber-900"}`}>All</button>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)} className={`px-2.5 py-1 rounded-md text-xs ${category === c ? "bg-amber-900 text-amber-50" : "bg-amber-100 text-amber-900"}`}>{c}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((c) => {
              const realIdx = FLASHCARDS.indexOf(c);
              const isFlipped = flipped[realIdx];
              return (
                <div key={realIdx} onClick={() => setFlipped({ ...flipped, [realIdx]: !isFlipped })} className="p-4 rounded-lg bg-white/80 border border-amber-900/20 cursor-pointer hover:shadow-md transition min-h-[120px] flex flex-col justify-center">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-1">{c.cat}</div>
                  {!isFlipped
                    ? <div className="text-amber-950 font-medium text-sm">{c.front}</div>
                    : <pre className="text-emerald-900 text-xs whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "inherit" }}>{c.back}</pre>
                  }
                </div>
              );
            })}
          </div>
        </>
      )}

      {mode === "quiz" && (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-2 rounded-full bg-amber-100 overflow-hidden">
              <div className="h-full bg-amber-700 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-xs text-amber-900">{quizIndex + 1} / {total}</span>
            <button onClick={shuffle} className="p-1.5 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-900" title="Shuffle"><Shuffle className="w-4 h-4" /></button>
            <button onClick={reset} className="p-1.5 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-900" title="Reset"><RotateCcw className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-4 text-xs text-amber-900 mb-3">
            <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-600 mr-1" />I know this: {knownCount}</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-rose-600 mr-1" />Still learning: {stillLearning}</span>
          </div>
          {quizIndex < total ? (
            <>
              <div onClick={() => setQuizFlipped(!quizFlipped)} className="p-6 rounded-xl bg-white/90 border-2 border-amber-900/30 cursor-pointer hover:shadow-lg transition min-h-[200px] flex flex-col justify-center items-center text-center">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-3">{card.cat} — click to flip</div>
                {!quizFlipped
                  ? <div className="text-amber-950 font-medium">{card.front}</div>
                  : <pre className="text-emerald-900 text-sm whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "inherit" }}>{card.back}</pre>
                }
              </div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => { setStillLearning(stillLearning + 1); setQuizIndex(quizIndex + 1); setQuizFlipped(false); }} className="flex-1 py-2 rounded-md bg-rose-100 text-rose-900 hover:bg-rose-200 text-sm font-medium">Still learning</button>
                <button onClick={() => { setKnownCount(knownCount + 1); setQuizIndex(quizIndex + 1); setQuizFlipped(false); }} className="flex-1 py-2 rounded-md bg-emerald-100 text-emerald-900 hover:bg-emerald-200 text-sm font-medium">I know this</button>
              </div>
            </>
          ) : (
            <div className="p-6 rounded-xl bg-white/90 border border-amber-900/20 text-center">
              <div className="text-2xl mb-2" style={{ fontFamily: "Palatino, serif" }}>Quiz complete</div>
              <p className="text-amber-900 mb-4">You knew <strong>{knownCount}</strong> of {total} · Still learning <strong>{stillLearning}</strong></p>
              <button onClick={reset} className="px-4 py-2 rounded-md bg-amber-900 text-amber-50 hover:bg-amber-950">Start again</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function StudyGuideLU5Gr10({ onBack }: { onBack?: () => void }) {
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [navOpen, setNavOpen] = useState(false);
  const toggle = (id: string) => setDone({ ...done, [id]: !done[id] });

  const chapters = [
    { id: "ch1",  label: "1. Algorithms & Problem-Solving" },
    { id: "ch2",  label: "2. Pseudocode" },
    { id: "ch3",  label: "3. Flowcharts" },
    { id: "ch4",  label: "4. Java Basics" },
    { id: "ch5",  label: "5. Input & Output" },
    { id: "ch6",  label: "6. Arithmetic & Casting" },
    { id: "ch7",  label: "7. Math.random() & Math.round()" },
    { id: "ch8",  label: "8. Selection (if / else)" },
    { id: "ch9",  label: "9. Iteration (loops)" },
    { id: "ch10", label: "10. Tracing Code" },
    { id: "ch11", label: "11. Flashcards" },
    { id: "ch12", label: "12. Practice Exercises" },
  ];

  const completed = Object.values(done).filter(Boolean).length;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #fdf6e3 0%, #faebd7 35%, #f5deb3 100%)", fontFamily: "Georgia, 'Times New Roman', serif" }}>

      {/* Sticky Header */}
      <header className="sticky top-0 z-30 bg-amber-950/95 backdrop-blur text-amber-50 shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-1.5 rounded hover:bg-amber-900 text-amber-200 text-sm flex items-center gap-1">
              ← Back
            </button>
          )}
          <button onClick={() => setNavOpen(!navOpen)} className="p-1.5 rounded hover:bg-amber-900">
            {navOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <BookOpen className="w-5 h-5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm sm:text-base font-semibold truncate" style={{ fontFamily: "Palatino, 'Palatino Linotype', Georgia, serif" }}>
              Solution Development — A Grade 10 Textbook
            </div>
            <div className="text-[10px] sm:text-xs text-amber-200/80 truncate">
              Helpmekaar Kollege · IT Grade 10 · LU5
            </div>
          </div>
          <div className="text-xs text-amber-200 whitespace-nowrap">{completed}/{chapters.length - 2} done</div>
        </div>
        {navOpen && (
          <nav className="max-w-5xl mx-auto px-4 pb-3 border-t border-amber-900/40">
            <ul className="py-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {chapters.map(c => (
                <li key={c.id}>
                  <a href={`#${c.id}`} onClick={() => setNavOpen(false)} className="block px-2 py-1.5 text-sm rounded hover:bg-amber-900 text-amber-50">{c.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">

        {/* Title */}
        <div className="mb-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-700 mb-2">Learning Unit 5</div>
          <h1 className="text-4xl md:text-5xl text-amber-950 font-bold leading-tight mb-3" style={{ fontFamily: "Palatino, 'Palatino Linotype', Georgia, serif" }}>
            Solution Development
          </h1>
          <p className="text-amber-800 italic max-w-2xl mx-auto">
            A complete Grade 10 textbook on computational thinking, algorithms, pseudocode, flowcharts and Java programming.
          </p>
          <div className="mt-4 inline-block text-xs text-amber-700 border-t border-b border-amber-900/30 px-6 py-1">
            Helpmekaar Kollege · 2026
          </div>
        </div>

        {/* ── Chapter 1 ─────────────────────────────────────────────────────── */}
        <Section id="ch1" title="Chapter 1 — Algorithms & Problem-Solving" subtitle="How a programmer thinks before a single line of code is written" completed={!!done.ch1} onToggleComplete={() => toggle("ch1")} defaultOpen>
          <p className="mb-3">
            Before any program is written, a programmer must first <em>understand the problem</em> and <em>plan a solution</em>. The code is the last step, not the first. The ability to break a problem into clear, ordered steps is called <strong>computational thinking</strong>, and the set of steps itself is called an <strong>algorithm</strong>.
          </p>
          <InfoBox variant="key" title="Definition: Algorithm">
            An algorithm is a <strong>finite</strong>, <strong>ordered</strong>, <strong>unambiguous</strong> sequence of steps that solves a problem or performs a task. "Finite" means it must end. "Ordered" means the steps happen in a specific sequence. "Unambiguous" means each step has exactly one meaning.
          </InfoBox>
          <p className="mb-3">
            You already use algorithms every day. The instructions on a packet of two-minute noodles are an algorithm: add water, boil, add noodles, cook for two minutes, add the flavour sachet, stir. Miss a step, or do them in the wrong order, and you get a worse result.
          </p>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>1.1 The IPO Model</h3>
          <p className="mb-3">Every program can be described using the <strong>IPO model</strong>:</p>
          <Table headers={["Stage", "What happens", "Example"]} rows={[
            ["Input", "The program receives data from the user or a file.", "The user types their age."],
            ["Processing", "The program does something with the data: calculates, compares, transforms.", "The program checks if age ≥ 18."],
            ["Output", "The program produces a result: shown on screen, saved, or printed.", 'The program displays "You may vote."'],
          ]} />
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>1.2 The Four Steps of Problem-Solving</h3>
          <ol className="list-decimal pl-6 space-y-2 mb-3">
            <li><strong>Understand the problem.</strong> What is the input? What is the expected output? Are there rules or limits? Read the problem twice.</li>
            <li><strong>Plan a solution.</strong> Write an algorithm in plain English, then turn it into pseudocode or a flowchart.</li>
            <li><strong>Code the solution.</strong> Translate the plan into Java.</li>
            <li><strong>Test and debug.</strong> Run the code with different inputs. Try edge cases: zero, negative numbers, empty input.</li>
          </ol>
          <InfoBox variant="tip" title="Practical tip">
            Most beginner mistakes come from skipping step 1 or 2. A five-minute plan saves an hour of debugging.
          </InfoBox>
          <Worked title="From problem to algorithm">
            <p className="mb-2"><strong>Problem:</strong> Write an algorithm that asks the user for two numbers and prints the larger one.</p>
            <p className="mb-1"><strong>Inputs:</strong> two numbers (a and b). <strong>Output:</strong> the larger number.</p>
            <p className="mb-2 mt-2"><strong>Algorithm:</strong></p>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Ask the user for the first number and store it as a.</li>
              <li>Ask the user for the second number and store it as b.</li>
              <li>If a is greater than b, then print a.</li>
              <li>Otherwise, print b.</li>
            </ol>
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>1.3 The Three Control Structures</h3>
          <Table headers={["Structure", "What it does", "Used when"]} rows={[
            ["Sequence", "Instructions execute one after another in order.", "Always — it is the default."],
            ["Selection", "A decision is made; different code runs depending on a condition.", "The program must choose between options."],
            ["Iteration", "A block of code repeats.", "The program must do something multiple times."],
          ]} />
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Check your understanding</h3>
          <p className="text-sm mb-1">Q1. Give a plain-English definition of the word "algorithm".</p>
          <Spoiler>A finite, ordered, unambiguous sequence of steps that solves a problem or performs a task.</Spoiler>
          <p className="text-sm mb-1">Q2. Name the three parts of the IPO model.</p>
          <Spoiler>Input, Processing, Output.</Spoiler>
          <p className="text-sm mb-1">Q3. Why is it a bad idea to start typing code before planning?</p>
          <Spoiler>Without a plan, the programmer does not fully understand the problem. They are likely to miss cases, get lost in details, and spend longer debugging than the plan would have taken.</Spoiler>
        </Section>

        {/* ── Chapter 2 ─────────────────────────────────────────────────────── */}
        <Section id="ch2" title="Chapter 2 — Pseudocode" subtitle="Planning a program in structured English" completed={!!done.ch2} onToggleComplete={() => toggle("ch2")}>
          <p className="mb-3">
            <strong>Pseudocode</strong> is halfway between English and a programming language. It uses plain English words with a <em>structured</em> layout — keywords, indentation, and clear blocks — so that it can be translated into real code easily.
          </p>
          <InfoBox variant="key" title="Pseudocode is not code">
            Pseudocode is <strong>not</strong> executable. You cannot run it on a computer. It is a planning tool.
          </InfoBox>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>2.1 The Basic Keywords</h3>
          <Table headers={["Keyword", "Meaning"]} rows={[
            ["BEGIN / END", "Marks the start and end of the pseudocode."],
            ["INPUT var", "Get a value from the user and store it in var."],
            ["DISPLAY or OUTPUT", "Show a message or a value on the screen."],
            ["SET var = value", "Store a value in a variable (assignment)."],
            ["IF ... THEN ... ELSE", "Selection (see section 2.3)."],
            ["FOR / WHILE / REPEAT", "Iteration (see section 2.4)."],
          ]} />
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>2.2 Sequence</h3>
          <Worked title="Greet the user">
            <Code>{`BEGIN
  DISPLAY "What is your name?"
  INPUT name
  DISPLAY "Hello, " + name + "!"
END`}</Code>
          </Worked>
          <Worked title="Average of three numbers">
            <Code>{`BEGIN
  DISPLAY "Enter three marks:"
  INPUT mark1
  INPUT mark2
  INPUT mark3
  SET total = mark1 + mark2 + mark3
  SET average = total / 3
  DISPLAY "The average is " + average
END`}</Code>
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>2.3 Selection with IF / ELSE IF / ELSE</h3>
          <Code>{`IF condition THEN
  (do this)
ELSE IF another_condition THEN
  (do this instead)
ELSE
  (do this if nothing else matched)
END IF`}</Code>
          <Worked title="Pass or fail">
            <Code>{`BEGIN
  DISPLAY "Enter your mark:"
  INPUT mark
  IF mark >= 50 THEN
    DISPLAY "Pass"
  ELSE
    DISPLAY "Fail"
  END IF
END`}</Code>
          </Worked>
          <Worked title="Grade from mark">
            <Code>{`BEGIN
  DISPLAY "Enter your mark:"
  INPUT mark
  IF mark >= 80 THEN
    DISPLAY "A"
  ELSE IF mark >= 70 THEN
    DISPLAY "B"
  ELSE IF mark >= 60 THEN
    DISPLAY "C"
  ELSE IF mark >= 50 THEN
    DISPLAY "D"
  ELSE
    DISPLAY "Fail"
  END IF
END`}</Code>
            <p>The conditions are checked in order. As soon as one is true, the program runs that block and skips all the rest. The order matters — check strictest first.</p>
          </Worked>
          <InfoBox variant="warn" title="Common mistake">
            Students often forget <Inline>END IF</Inline>, or indent blocks inconsistently. Be strict: every <Inline>IF</Inline> gets an <Inline>END IF</Inline>, and every block inside a structure is indented.
          </InfoBox>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>2.4 Iteration</h3>
          <Table headers={["Loop", "Use when", "Shape"]} rows={[
            ["FOR", "You know exactly how many repetitions.", <Code>{`FOR i = 1 TO 5\n  DISPLAY i\nNEXT i`}</Code>],
            ["WHILE", "Repeat while a condition is true. Tested before the loop body.", <Code>{`WHILE count < 10\n  SET count = count + 1\nEND WHILE`}</Code>],
            ["REPEAT", "Repeat until a condition is true. Tested after the loop body.", <Code>{`REPEAT\n  INPUT answer\nUNTIL answer = "stop"`}</Code>],
          ]} />
          <Worked title="Keep asking until the user types 0">
            <Code>{`BEGIN
  SET num = 1
  WHILE num <> 0
    DISPLAY "Enter a number (0 to stop):"
    INPUT num
  END WHILE
  DISPLAY "Goodbye"
END`}</Code>
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Check your understanding</h3>
          <p className="text-sm mb-1">Q1. Write pseudocode that reads two numbers and displays their sum.</p>
          <Spoiler><Code>{`BEGIN\n  INPUT a\n  INPUT b\n  SET sum = a + b\n  DISPLAY sum\nEND`}</Code></Spoiler>
          <p className="text-sm mb-1">Q2. Write pseudocode that reads an age and displays "child" if under 13, "teen" if 13–19, otherwise "adult".</p>
          <Spoiler><Code>{`BEGIN\n  INPUT age\n  IF age < 13 THEN\n    DISPLAY "child"\n  ELSE IF age <= 19 THEN\n    DISPLAY "teen"\n  ELSE\n    DISPLAY "adult"\n  END IF\nEND`}</Code></Spoiler>

          <LU5PseudocodeExercises />
        </Section>

        {/* ── Chapter 3 ─────────────────────────────────────────────────────── */}
        <Section id="ch3" title="Chapter 3 — Flowcharts" subtitle="Drawing an algorithm as a diagram" completed={!!done.ch3} onToggleComplete={() => toggle("ch3")}>
          <p className="mb-3">A <strong>flowchart</strong> is a picture of an algorithm. Every step is drawn as a shape, and arrows show the direction the program flows.</p>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>3.1 The Four Shapes</h3>
          <Table headers={["Shape", "Name", "Used for", "Example text"]} rows={[
            ["Oval", "Terminator", "Start or end of the flowchart", "START / END"],
            ["Parallelogram", "I/O", "Input from user, output to screen", "INPUT age / DISPLAY total"],
            ["Rectangle", "Process", "Calculation or assignment", "total = a + b"],
            ["Diamond", "Decision", "A yes/no question", "age >= 18 ?"],
          ]} />
          <div className="my-5 flex justify-center">
            <svg viewBox="0 0 500 380" className="max-w-md w-full">
              <ellipse cx="100" cy="40" rx="55" ry="22" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
              <text x="100" y="45" textAnchor="middle" fontSize="13" fill="#78350f" fontFamily="Georgia, serif">START</text>
              <text x="200" y="45" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">Oval = terminator (start/end)</text>
              <polygon points="70,100 160,100 140,140 50,140" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
              <text x="105" y="124" textAnchor="middle" fontSize="13" fill="#78350f" fontFamily="Georgia, serif">INPUT age</text>
              <text x="200" y="124" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">Parallelogram = input/output</text>
              <rect x="50" y="170" width="110" height="40" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
              <text x="105" y="194" textAnchor="middle" fontSize="13" fill="#78350f" fontFamily="Georgia, serif">total = a + b</text>
              <text x="200" y="194" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">Rectangle = process</text>
              <polygon points="105,235 165,280 105,325 45,280" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
              <text x="105" y="284" textAnchor="middle" fontSize="12" fill="#78350f" fontFamily="Georgia, serif">age ≥ 18?</text>
              <text x="200" y="284" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">Diamond = decision</text>
              <line x1="260" y1="340" x2="310" y2="340" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrow)"/>
              <text x="320" y="344" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">Arrow = flow line</text>
              <defs>
                <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#78350f"/>
                </marker>
              </defs>
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>3.2 Rules for Drawing Flowcharts</h3>
          <ol className="list-decimal pl-6 space-y-1 mb-3">
            <li>Every flowchart starts with <strong>START</strong> and ends with <strong>END</strong> in ovals.</li>
            <li>Arrows have direction (an arrowhead). They never cross unless absolutely necessary.</li>
            <li>A decision diamond has <strong>exactly one arrow in</strong> and <strong>exactly two arrows out</strong>, labelled Yes/No.</li>
            <li>Flow is top to bottom, or left to right. Consistency matters.</li>
          </ol>

          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>3.3 The Three Control Structures</h3>
          <p className="mb-3">Every algorithm — no matter how complex — is built from three basic patterns. You must be able to recognise and draw each one.</p>
          <Table headers={["Structure", "What it does", "Flowchart pattern"]} rows={[
            ["Sequence", "Instructions run one after another, top to bottom.", "Shapes stacked vertically with arrows going straight down."],
            ["Selection", "A decision chooses between two paths.", "A diamond with Yes / No branches that re-join afterwards."],
            ["Iteration", "Instructions repeat while a condition is true.", "A diamond whose Yes branch loops back up to an earlier step."],
          ]} />
          <div className="my-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* SEQUENCE */}
            <div className="flex flex-col items-center p-3 rounded-lg bg-amber-100/40 border border-amber-300">
              <p className="text-sm font-semibold text-amber-900 mb-2">Sequence</p>
              <svg viewBox="0 0 160 220" className="w-full max-w-[160px]">
                <defs>
                  <marker id="arrseq" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#78350f"/>
                  </marker>
                </defs>
                <ellipse cx="80" cy="18" rx="38" ry="13" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="80" y="22" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">START</text>
                <line x1="80" y1="31" x2="80" y2="50" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrseq)"/>
                <rect x="30" y="55" width="100" height="25" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="80" y="72" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">step 1</text>
                <line x1="80" y1="80" x2="80" y2="100" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrseq)"/>
                <rect x="30" y="105" width="100" height="25" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="80" y="122" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">step 2</text>
                <line x1="80" y1="130" x2="80" y2="150" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrseq)"/>
                <rect x="30" y="155" width="100" height="25" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="80" y="172" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">step 3</text>
                <line x1="80" y1="180" x2="80" y2="198" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrseq)"/>
                <ellipse cx="80" cy="208" rx="38" ry="11" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="80" y="212" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">END</text>
              </svg>
            </div>
            {/* SELECTION */}
            <div className="flex flex-col items-center p-3 rounded-lg bg-amber-100/40 border border-amber-300">
              <p className="text-sm font-semibold text-amber-900 mb-2">Selection</p>
              <svg viewBox="0 0 200 240" className="w-full max-w-[200px]">
                <defs>
                  <marker id="arrsel" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#78350f"/>
                  </marker>
                </defs>
                <ellipse cx="100" cy="18" rx="38" ry="13" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="100" y="22" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">START</text>
                <line x1="100" y1="31" x2="100" y2="48" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrsel)"/>
                <polygon points="100,52 150,90 100,128 50,90" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="100" y="94" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">cond?</text>
                <line x1="50" y1="90" x2="25" y2="90" stroke="#78350f" strokeWidth="2"/>
                <text x="28" y="84" fontSize="10" fill="#78350f" fontFamily="Georgia, serif">Yes</text>
                <line x1="25" y1="90" x2="25" y2="135" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrsel)"/>
                <rect x="0" y="140" width="50" height="25" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="25" y="157" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">A</text>
                <line x1="150" y1="90" x2="175" y2="90" stroke="#78350f" strokeWidth="2"/>
                <text x="156" y="84" fontSize="10" fill="#78350f" fontFamily="Georgia, serif">No</text>
                <line x1="175" y1="90" x2="175" y2="135" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrsel)"/>
                <rect x="150" y="140" width="50" height="25" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="175" y="157" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">B</text>
                <line x1="25" y1="165" x2="25" y2="195" stroke="#78350f" strokeWidth="2"/>
                <line x1="175" y1="165" x2="175" y2="195" stroke="#78350f" strokeWidth="2"/>
                <line x1="25" y1="195" x2="175" y2="195" stroke="#78350f" strokeWidth="2"/>
                <line x1="100" y1="195" x2="100" y2="212" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrsel)"/>
                <ellipse cx="100" cy="222" rx="38" ry="11" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="100" y="226" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">END</text>
              </svg>
            </div>
            {/* ITERATION */}
            <div className="flex flex-col items-center p-3 rounded-lg bg-amber-100/40 border border-amber-300">
              <p className="text-sm font-semibold text-amber-900 mb-2">Iteration (Loop)</p>
              <svg viewBox="0 0 200 260" className="w-full max-w-[200px]">
                <defs>
                  <marker id="arrit" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#78350f"/>
                  </marker>
                </defs>
                <ellipse cx="100" cy="18" rx="38" ry="13" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="100" y="22" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">START</text>
                <line x1="100" y1="31" x2="100" y2="60" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrit)"/>
                <polygon points="100,65 155,105 100,145 45,105" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="100" y="109" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">cond?</text>
                <line x1="100" y1="145" x2="100" y2="162" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrit)"/>
                <text x="105" y="158" fontSize="10" fill="#78350f" fontFamily="Georgia, serif">Yes</text>
                <rect x="50" y="167" width="100" height="25" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="100" y="184" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">body</text>
                <line x1="50" y1="180" x2="15" y2="180" stroke="#78350f" strokeWidth="2"/>
                <line x1="15" y1="180" x2="15" y2="105" stroke="#78350f" strokeWidth="2"/>
                <line x1="15" y1="105" x2="43" y2="105" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrit)"/>
                <line x1="155" y1="105" x2="180" y2="105" stroke="#78350f" strokeWidth="2"/>
                <text x="158" y="99" fontSize="10" fill="#78350f" fontFamily="Georgia, serif">No</text>
                <line x1="180" y1="105" x2="180" y2="232" stroke="#78350f" strokeWidth="2"/>
                <line x1="180" y1="232" x2="138" y2="232" stroke="#78350f" strokeWidth="2" markerEnd="url(#arrit)"/>
                <ellipse cx="100" cy="232" rx="38" ry="13" fill="#fef3c7" stroke="#78350f" strokeWidth="2"/>
                <text x="100" y="236" textAnchor="middle" fontSize="11" fill="#78350f" fontFamily="Georgia, serif">END</text>
              </svg>
            </div>
          </div>
          <p className="text-sm text-amber-800/80 italic mb-3">The arrow that goes back <strong>up</strong> is how you spot a loop at a glance.</p>

          <Worked title="Flowchart: is a number positive, negative or zero?">
            <div className="flex justify-center">
              <svg viewBox="0 0 500 540" className="max-w-sm w-full">
                <defs>
                  <marker id="arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#4c1d95"/>
                  </marker>
                </defs>
                <ellipse cx="250" cy="30" rx="55" ry="20" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="250" y="35" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">START</text>
                <line x1="250" y1="50" x2="250" y2="75" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arr)"/>
                <polygon points="190,80 320,80 300,115 210,115" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="255" y="102" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">INPUT num</text>
                <line x1="255" y1="115" x2="255" y2="140" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arr)"/>
                <polygon points="255,145 335,200 255,255 175,200" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="255" y="205" textAnchor="middle" fontSize="12" fill="#4c1d95" fontFamily="Georgia, serif">num &gt; 0 ?</text>
                <line x1="335" y1="200" x2="410" y2="200" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arr)"/>
                <text x="345" y="193" fontSize="11" fill="#4c1d95" fontFamily="Georgia, serif">Yes</text>
                <polygon points="370,215 470,215 455,250 355,250" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="410" y="236" textAnchor="middle" fontSize="12" fill="#4c1d95" fontFamily="Georgia, serif">DISPLAY "positive"</text>
                <line x1="255" y1="255" x2="255" y2="285" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arr)"/>
                <text x="260" y="275" fontSize="11" fill="#4c1d95" fontFamily="Georgia, serif">No</text>
                <polygon points="255,290 335,340 255,390 175,340" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="255" y="345" textAnchor="middle" fontSize="12" fill="#4c1d95" fontFamily="Georgia, serif">num &lt; 0 ?</text>
                <line x1="175" y1="340" x2="115" y2="340" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arr)"/>
                <text x="130" y="333" fontSize="11" fill="#4c1d95" fontFamily="Georgia, serif">Yes</text>
                <polygon points="40,325 140,325 125,360 25,360" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="82" y="346" textAnchor="middle" fontSize="11" fill="#4c1d95" fontFamily="Georgia, serif">DISPLAY "negative"</text>
                <line x1="255" y1="390" x2="255" y2="420" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arr)"/>
                <text x="260" y="410" fontSize="11" fill="#4c1d95" fontFamily="Georgia, serif">No</text>
                <polygon points="200,425 310,425 295,460 185,460" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="247" y="446" textAnchor="middle" fontSize="12" fill="#4c1d95" fontFamily="Georgia, serif">DISPLAY "zero"</text>
                <line x1="247" y1="460" x2="247" y2="495" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arr)"/>
                <line x1="82" y1="360" x2="82" y2="485" stroke="#4c1d95" strokeWidth="2"/>
                <line x1="82" y1="485" x2="247" y2="485" stroke="#4c1d95" strokeWidth="2"/>
                <line x1="410" y1="250" x2="410" y2="485" stroke="#4c1d95" strokeWidth="2"/>
                <line x1="410" y1="485" x2="247" y2="485" stroke="#4c1d95" strokeWidth="2"/>
                <ellipse cx="247" cy="510" rx="50" ry="18" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="247" y="515" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">END</text>
              </svg>
            </div>
          </Worked>

          <Worked title="Flowchart: print the numbers 1 to 5 (a loop)">
            <p className="text-sm mb-3">This is your first <strong>iteration</strong> flowchart. A counter variable <code>count</code> starts at 1, and every time through the loop we display it and add 1. When <code>count</code> is no longer ≤ 5, we exit the loop.</p>
            <div className="flex justify-center">
              <svg viewBox="0 0 420 540" className="max-w-sm w-full">
                <defs>
                  <marker id="arrloop" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#4c1d95"/>
                  </marker>
                </defs>
                <ellipse cx="210" cy="30" rx="55" ry="20" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="210" y="35" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">START</text>
                <line x1="210" y1="50" x2="210" y2="75" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrloop)"/>
                <rect x="150" y="80" width="120" height="35" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="210" y="103" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">count = 1</text>
                <line x1="210" y1="115" x2="210" y2="140" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrloop)"/>
                <polygon points="210,145 290,200 210,255 130,200" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="210" y="205" textAnchor="middle" fontSize="12" fill="#4c1d95" fontFamily="Georgia, serif">count ≤ 5 ?</text>
                <line x1="210" y1="255" x2="210" y2="280" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrloop)"/>
                <text x="215" y="275" fontSize="11" fill="#4c1d95" fontFamily="Georgia, serif">Yes</text>
                <polygon points="150,285 280,285 260,320 170,320" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="215" y="307" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">DISPLAY count</text>
                <line x1="215" y1="320" x2="215" y2="345" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrloop)"/>
                <rect x="150" y="350" width="120" height="35" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="210" y="373" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">count = count + 1</text>
                <line x1="150" y1="367" x2="80" y2="367" stroke="#4c1d95" strokeWidth="2"/>
                <line x1="80" y1="367" x2="80" y2="140" stroke="#4c1d95" strokeWidth="2"/>
                <line x1="80" y1="140" x2="128" y2="140" stroke="#4c1d95" strokeWidth="2"/>
                <line x1="128" y1="140" x2="158" y2="175" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrloop)"/>
                <line x1="290" y1="200" x2="350" y2="200" stroke="#4c1d95" strokeWidth="2"/>
                <text x="295" y="193" fontSize="11" fill="#4c1d95" fontFamily="Georgia, serif">No</text>
                <line x1="350" y1="200" x2="350" y2="450" stroke="#4c1d95" strokeWidth="2"/>
                <line x1="350" y1="450" x2="265" y2="450" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrloop)"/>
                <ellipse cx="210" cy="450" rx="55" ry="20" fill="#ede9fe" stroke="#4c1d95" strokeWidth="2"/>
                <text x="210" y="455" textAnchor="middle" fontSize="13" fill="#4c1d95" fontFamily="Georgia, serif">END</text>
              </svg>
            </div>
            <p className="text-sm mt-3"><strong>What will this print?</strong></p>
            <Spoiler>1, then 2, then 3, then 4, then 5 (each on its own line). After printing 5, <code>count</code> becomes 6, the condition <code>count ≤ 5</code> is false, so the program takes the No branch and reaches END.</Spoiler>
          </Worked>

          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>3.4 Tracing a Flowchart</h3>
          <p className="mb-3"><strong>Tracing</strong> a flowchart means following it step-by-step with a specific input, and writing down exactly what would be displayed. Expect a test question like "<em>What does this flowchart output when x = 7?</em>"</p>
          <div className="p-3 rounded-md bg-amber-100/60 border border-amber-300 text-sm mb-3">
            <p className="font-semibold mb-1">Trace this flowchart:</p>
            <p>START → INPUT x → Is <code>x &gt; 10</code>? → <strong>Yes:</strong> DISPLAY "big" · <strong>No:</strong> DISPLAY "small" → END</p>
          </div>
          <p className="text-sm mb-1">Q1. What is displayed when x = 15?</p>
          <Spoiler>"big" — because 15 &gt; 10 is true, so the Yes branch runs.</Spoiler>
          <p className="text-sm mb-1">Q2. What is displayed when x = 4?</p>
          <Spoiler>"small" — because 4 &gt; 10 is false, so the No branch runs.</Spoiler>
          <p className="text-sm mb-1">Q3. What is displayed when x = 10?</p>
          <Spoiler>"small" — 10 &gt; 10 is <em>false</em> (it's greater-than, not greater-than-or-equal). Edge cases like this are where marks are lost — always read the operator carefully.</Spoiler>

          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>3.5 Pseudocode ↔ Flowchart</h3>
          <p className="mb-3">Pseudocode and flowcharts are two ways to describe the same algorithm. You must be able to translate between them.</p>
          <Table headers={["Pseudocode", "Flowchart pattern"]} rows={[
            ["Two statements one after another", "Two rectangles stacked vertically, connected by a downward arrow."],
            ["IF condition THEN ... ELSE ... END IF", "A diamond labelled with the condition; one rectangle on each branch; the branches re-join afterwards."],
            ["WHILE condition DO ... END WHILE", "A diamond labelled with the condition; the Yes branch leads to the loop body and then back up to the diamond; the No branch exits the loop."],
          ]} />

          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Check your understanding</h3>
          <p className="text-sm mb-1">Q1. Name the four flowchart shapes and what each is used for.</p>
          <Spoiler>Oval — start/end (terminator). Parallelogram — input/output. Rectangle — process. Diamond — decision.</Spoiler>
          <p className="text-sm mb-1">Q2. How many arrows leave a decision diamond, and why?</p>
          <Spoiler>Exactly two. One for the Yes/True path and one for the No/False path.</Spoiler>
          <p className="text-sm mb-1">Q3. In a flowchart, how can you tell at a glance that there is a loop?</p>
          <Spoiler>There is an arrow that goes <strong>back up</strong> — from a point below the decision diamond, returning to a point above it. That is the visual signature of iteration.</Spoiler>

          <LU5FlowchartExercises />
        </Section>

        {/* ── Chapter 4 ─────────────────────────────────────────────────────── */}
        <Section id="ch4" title="Chapter 4 — Java Basics" subtitle="Variables, data types and the shape of a Java program" completed={!!done.ch4} onToggleComplete={() => toggle("ch4")}>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>4.1 A Minimal Java Program</h3>
          <Code>{`public class Hello {\n    public static void main(String[] args) {\n        System.out.println("Hello, world!");\n    }\n}`}</Code>
          <p className="mb-3">All your code goes <em>inside</em> the <Inline>main</Inline> method. You can mostly ignore the two outer lines (but must still type them).</p>
          <InfoBox variant="key" title="Things to notice">
            <ul className="list-disc pl-5 space-y-1">
              <li>Every statement ends with a <strong>semicolon</strong> ( ; ).</li>
              <li>Blocks are enclosed in <strong>curly braces</strong> {"{ }"}.</li>
              <li>Java is <strong>case-sensitive</strong>. <Inline>System</Inline> ≠ <Inline>system</Inline>.</li>
              <li>Strings are surrounded by <strong>double quotes</strong>.</li>
            </ul>
          </InfoBox>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>4.2 Variables and Data Types</h3>
          <Code>{`int age = 15;\ndouble price = 29.99;\nString name = "Sipho";\nboolean passed = true;\nchar grade = 'A';`}</Code>
          <Table headers={["Data type", "Stores", "Example value"]} rows={[
            ["int", "Whole numbers (positive or negative)", "15, -3, 0, 1000"],
            ["double", "Decimal numbers", "29.99, 3.14, -0.5"],
            ["String", "Text (any length)", '"Hello", "Pretoria"'],
            ["boolean", "A true/false value", "true, false"],
            ["char", "A single character", "'A', '?', '7'"],
          ]} />
          <InfoBox variant="warn" title="Strings vs chars">
            Strings use <strong>double quotes</strong>: <Inline>"A"</Inline>. Chars use <strong>single quotes</strong>: <Inline>'A'</Inline>.
          </InfoBox>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>4.3 The Assignment Operator</h3>
          <Code>{`int x = 5;        // x now holds 5\nx = x + 3;        // x now holds 8\nx = 10;           // x now holds 10`}</Code>
          <InfoBox variant="warn" title="= vs ==">
            <Inline>=</Inline> is <strong>assignment</strong>. <Inline>==</Inline> is <strong>comparison</strong>. Mixing these up is one of the classic bugs.
          </InfoBox>
          <Worked title="Tracing variable values">
            <Code>{`int a = 5;\nint b = 10;\na = b;\nb = 20;`}</Code>
            <Table headers={["Line", "a", "b"]} rows={[
              ["int a = 5;", "5", "—"],
              ["int b = 10;", "5", "10"],
              ["a = b;", "10", "10"],
              ["b = 20;", "10", "20"],
            ]} />
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Check your understanding</h3>
          <p className="text-sm mb-1">Q1. Which data type would you use to store a learner's height in metres?</p>
          <Spoiler><Inline>double</Inline> — because height has a decimal part.</Spoiler>
          <p className="text-sm mb-1">Q2. Which data type would you use to store whether a learner has paid school fees?</p>
          <Spoiler><Inline>boolean</Inline> — because the answer is either true or false.</Spoiler>
        </Section>

        {/* ── Chapter 5 ─────────────────────────────────────────────────────── */}
        <Section id="ch5" title="Chapter 5 — Input & Output" subtitle="Getting information in and out of a Java program" completed={!!done.ch5} onToggleComplete={() => toggle("ch5")}>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>5.1 Output with System.out</h3>
          <Code>{`System.out.println("Hello!");       // prints Hello! and moves to next line\nSystem.out.print("No newline here"); // stays on same line\nSystem.out.println();                // prints a blank line`}</Code>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>5.2 Concatenation</h3>
          <Code>{`String name = "Lerato";\nint age = 15;\nSystem.out.println("Hello, " + name + "! You are " + age + " years old.");`}</Code>
          <InfoBox variant="tip" title="Why the spaces?">
            Look at <Inline>"Hello, "</Inline> — there is a space before the closing quote. Without it the output would run words together.
          </InfoBox>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>5.3 Input with Scanner</h3>
          <Code>{`import java.util.Scanner;\n\npublic class AgeProgram {\n    public static void main(String[] args) {\n        Scanner input = new Scanner(System.in);\n\n        System.out.print("Enter your age: ");\n        int age = input.nextInt();\n\n        System.out.println("You are " + age + " years old.");\n    }\n}`}</Code>
          <Table headers={["Method", "Reads", "Stores in"]} rows={[
            ["input.nextInt()", "A whole number", "int"],
            ["input.nextDouble()", "A decimal number", "double"],
            ["input.next()", "A single word (no spaces)", "String"],
            ["input.nextLine()", "A whole line of text", "String"],
          ]} />
        </Section>

        {/* ── Chapter 6 ─────────────────────────────────────────────────────── */}
        <Section id="ch6" title="Chapter 6 — Arithmetic & Casting" subtitle="Doing maths, and converting between data types" completed={!!done.ch6} onToggleComplete={() => toggle("ch6")}>
          <Table headers={["Operator", "Meaning", "Example", "Result"]} rows={[
            ["+", "Addition", "7 + 3", "10"],
            ["-", "Subtraction", "7 - 3", "4"],
            ["*", "Multiplication", "7 * 3", "21"],
            ["/", "Division", "7 / 3", "2 (integer)"],
            ["%", "Modulus (remainder)", "7 % 3", "1"],
          ]} />
          <InfoBox variant="key" title="Integer vs Double division">
            If both operands of <Inline>/</Inline> are integers, the result is an integer (decimal chopped). If at least one is a <Inline>double</Inline>, the result is a <Inline>double</Inline>.
          </InfoBox>
          <Code>{`int result = 7 / 3;        // result is 2, not 2.33\ndouble result3 = 7.0 / 3;  // 2.333... — because 7.0 is a double`}</Code>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Casting</h3>
          <Code>{`double d = 3.7;\nint n = (int) d;           // n is 3 (decimal chopped, not rounded)\n\nint total = 17;\nint count = 5;\ndouble avg = (double) total / count;   // 3.4`}</Code>
          <InfoBox variant="warn" title="Casting truncates, it does not round">
            <Inline>(int) 3.9</Inline> gives <Inline>3</Inline>, not 4. To round properly, use <Inline>Math.round()</Inline>.
          </InfoBox>
        </Section>

        {/* ── Chapter 7 ─────────────────────────────────────────────────────── */}
        <Section id="ch7" title="Chapter 7 — Math.random() & Math.round()" subtitle="Random numbers, and rounding properly" completed={!!done.ch7} onToggleComplete={() => toggle("ch7")}>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>7.1 Math.random()</h3>
          <p className="mb-3"><Inline>Math.random()</Inline> returns a random double between <Inline>0.0</Inline> (inclusive) and <Inline>1.0</Inline> (exclusive).</p>
          <InfoBox variant="key" title="The random-integer formula">
            <p className="mb-1">To get a random integer in the range [min, max] inclusive:</p>
            <Code>{`int n = (int)(Math.random() * (max - min + 1)) + min;`}</Code>
          </InfoBox>
          <Table headers={["You want", "Formula"]} rows={[
            ["Random int from 0 to 9",   "(int)(Math.random() * 10)"],
            ["Random int from 1 to 10",  "(int)(Math.random() * 10) + 1"],
            ["Random int from 1 to 6 (dice)", "(int)(Math.random() * 6) + 1"],
            ["Random int from 0 to 100", "(int)(Math.random() * 101)"],
            ["Random int from 50 to 99", "(int)(Math.random() * 50) + 50"],
          ]} />
          <Worked title="Simulate rolling a six-sided die">
            <Code>{`int roll = (int)(Math.random() * 6) + 1;\nSystem.out.println("You rolled a " + roll);`}</Code>
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>7.2 Math.round()</h3>
          <Code>{`int a = (int) Math.round(3.4);   // 3\nint b = (int) Math.round(3.5);   // 4\nint c = (int) Math.round(3.9);   // 4`}</Code>
          <InfoBox variant="tip" title="Round versus cast">
            <Inline>(int) 3.9</Inline> gives 3 (truncation). <Inline>(int) Math.round(3.9)</Inline> gives 4 (proper rounding). Use <Inline>Math.round()</Inline> for money, marks, and real-world measurements.
          </InfoBox>
        </Section>

        {/* ── Chapter 8 ─────────────────────────────────────────────────────── */}
        <Section id="ch8" title="Chapter 8 — Selection (if / else)" subtitle="Making decisions in Java" completed={!!done.ch8} onToggleComplete={() => toggle("ch8")}>
          <Code>{`if (condition) {\n    // runs if condition is true\n}`}</Code>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>8.2 Comparison Operators</h3>
          <Table headers={["Operator", "Meaning", "Example", "Result (x=5)"]} rows={[
            ["==", "equal to",               "x == 5", "true"],
            ["!=", "not equal to",            "x != 5", "false"],
            [">",  "greater than",            "x > 3",  "true"],
            ["<",  "less than",               "x < 3",  "false"],
            [">=", "greater than or equal to","x >= 5", "true"],
            ["<=", "less than or equal to",   "x <= 4", "false"],
          ]} />
          <InfoBox variant="warn" title="Comparing Strings">
            Do <strong>not</strong> use <Inline>==</Inline> to compare strings. Use <Inline>.equals()</Inline>:
            <Code>{`if (name.equals("Sipho")) { ... }`}</Code>
          </InfoBox>
          <Worked title="Grade from mark (Java)">
            <Code>{`int mark = 73;\nif (mark >= 80) {\n    System.out.println("A");\n} else if (mark >= 70) {\n    System.out.println("B");\n} else if (mark >= 60) {\n    System.out.println("C");\n} else if (mark >= 50) {\n    System.out.println("D");\n} else {\n    System.out.println("Fail");\n}`}</Code>
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>8.5 Logical Operators</h3>
          <Table headers={["Operator", "Meaning", "Example", "True when…"]} rows={[
            ["&&", "AND", "age >= 13 && age <= 19", "age is both ≥ 13 and ≤ 19"],
            ["||", "OR",  "day == 6 || day == 7",   "day is 6 OR 7"],
            ["!",  "NOT", "!loggedIn",               "loggedIn is false"],
          ]} />
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Check your understanding</h3>
          <p className="text-sm mb-1">Q1. Write an if-else that prints "Even" if <Inline>n</Inline> is even, otherwise "Odd".</p>
          <Spoiler><Code>{`if (n % 2 == 0) {\n    System.out.println("Even");\n} else {\n    System.out.println("Odd");\n}`}</Code></Spoiler>
        </Section>

        {/* ── Chapter 9 ─────────────────────────────────────────────────────── */}
        <Section id="ch9" title="Chapter 9 — Iteration (loops)" subtitle="Repeating code with for and while" completed={!!done.ch9} onToggleComplete={() => toggle("ch9")}>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>9.1 The for Loop</h3>
          <Code>{`for (initialisation; condition; update) {\n    // code to repeat\n}`}</Code>
          <Worked title="Print 1 to 5">
            <Code>{`for (int i = 1; i <= 5; i++) {\n    System.out.println(i);\n}`}</Code>
            <Table headers={["Step", "i", "Condition i ≤ 5", "Action"]} rows={[
              ["Init",   "1", "true",  "Print 1"],
              ["Update", "2", "true",  "Print 2"],
              ["Update", "3", "true",  "Print 3"],
              ["Update", "4", "true",  "Print 4"],
              ["Update", "5", "true",  "Print 5"],
              ["Update", "6", "false", "Loop ends"],
            ]} />
          </Worked>
          <Worked title="Sum the numbers 1 to 10">
            <Code>{`int sum = 0;\nfor (int i = 1; i <= 10; i++) {\n    sum = sum + i;\n}\nSystem.out.println("Sum = " + sum);`}</Code>
            <p>This pattern — start at 0, add in a loop — is called an <strong>accumulator</strong>. You will use it constantly.</p>
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>9.2 The while Loop</h3>
          <Code>{`while (condition) {\n    // code to repeat\n}`}</Code>
          <Worked title="Keep asking for a positive number">
            <Code>{`Scanner input = new Scanner(System.in);\nint n = -1;\nwhile (n < 0) {\n    System.out.print("Enter a positive number: ");\n    n = input.nextInt();\n}\nSystem.out.println("Thanks! You entered " + n);`}</Code>
          </Worked>
          <InfoBox variant="warn" title="Infinite loops">
            If the loop variable is never updated, the loop will never end. Always check that <em>something inside the loop will eventually make the condition false</em>.
          </InfoBox>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Check your understanding</h3>
          <p className="text-sm mb-1">Q1. Write a for loop that prints 20, 19, 18, ..., 1.</p>
          <Spoiler><Code>{`for (int i = 20; i >= 1; i--) {\n    System.out.println(i);\n}`}</Code></Spoiler>
          <p className="text-sm mb-1">Q2. Write a for loop that adds the numbers 1 to 100 and prints the total.</p>
          <Spoiler><Code>{`int total = 0;\nfor (int i = 1; i <= 100; i++) {\n    total = total + i;\n}\nSystem.out.println(total);  // 5050`}</Code></Spoiler>
        </Section>

        {/* ── Chapter 10 ────────────────────────────────────────────────────── */}
        <Section id="ch10" title="Chapter 10 — Tracing Code" subtitle="Predicting output by hand" completed={!!done.ch10} onToggleComplete={() => toggle("ch10")}>
          <InfoBox variant="key" title="The golden rule">
            Never guess the output. <em>Always</em> draw a trace table with one column per variable.
          </InfoBox>
          <Worked title="Trace this code">
            <Code>{`int a = 3;\nint b = 5;\nint c = a + b;\na = a * 2;\nb = a - c;\nSystem.out.println(a);\nSystem.out.println(b);\nSystem.out.println(c);`}</Code>
            <Table headers={["Line", "a", "b", "c", "Output"]} rows={[
              ["int a = 3;",    "3",  "—",  "—",  "—"],
              ["int b = 5;",    "3",  "5",  "—",  "—"],
              ["int c = a + b;","3",  "5",  "8",  "—"],
              ["a = a * 2;",    "6",  "5",  "8",  "—"],
              ["b = a - c;",    "6",  "-2", "8",  "—"],
              ["println(a);",   "6",  "-2", "8",  "6"],
              ["println(b);",   "6",  "-2", "8",  "-2"],
              ["println(c);",   "6",  "-2", "8",  "8"],
            ]} />
          </Worked>
          <Worked title="Trace this for loop">
            <Code>{`int total = 0;\nfor (int i = 1; i <= 4; i++) {\n    total = total + i;\n    System.out.println("i=" + i + " total=" + total);\n}`}</Code>
            <Table headers={["Iteration", "i", "total (after)", "Output"]} rows={[
              ["1", "1", "0 + 1 = 1",  "i=1 total=1"],
              ["2", "2", "1 + 2 = 3",  "i=2 total=3"],
              ["3", "3", "3 + 3 = 6",  "i=3 total=6"],
              ["4", "4", "6 + 4 = 10", "i=4 total=10"],
            ]} />
          </Worked>
          <h3 className="text-lg font-semibold text-amber-950 mt-5 mb-2" style={{ fontFamily: "Palatino, serif" }}>Check your understanding</h3>
          <p className="text-sm mb-1">Q1. What does this code print?</p>
          <Code>{`int x = 10;\nint y = 3;\nSystem.out.println(x / y);\nSystem.out.println(x % y);`}</Code>
          <Spoiler><Code>{`3\n1`}</Code><p><Inline>10 / 3 = 3</Inline> (integer division). <Inline>10 % 3 = 1</Inline> (remainder).</p></Spoiler>
          <p className="text-sm mb-1">Q2. What does this loop print?</p>
          <Code>{`for (int i = 1; i <= 5; i++) {\n    if (i % 2 == 0) {\n        System.out.println(i);\n    }\n}`}</Code>
          <Spoiler><Code>{`2\n4`}</Code></Spoiler>
        </Section>

        {/* ── Chapter 11 — Flashcards ───────────────────────────────────────── */}
        <Section id="ch11" title="Chapter 11 — Flashcards" subtitle="Drill the key facts and definitions" completed={!!done.ch11} onToggleComplete={() => toggle("ch11")}>
          <p className="mb-3">Use <strong>Browse</strong> to glance through by category, or <strong>Quiz</strong> to work through them randomly.</p>
          <CheatSheet />
        </Section>

        {/* ── Chapter 12 — Practice Exercises ──────────────────────────────── */}
        <Section id="ch12" title="Chapter 12 — Practice Exercises" subtitle="Ten mixed problems drawing on all chapters" completed={!!done.ch12} onToggleComplete={() => toggle("ch12")}>
          <p className="mb-3">Try each one yourself <em>before</em> revealing the answer.</p>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 1 — Pseudocode (sequential).</strong> Write pseudocode that reads the length and breadth of a rectangle, then calculates and displays its area and perimeter.</div>
          <Spoiler><Code>{`BEGIN\n  INPUT length\n  INPUT breadth\n  SET area = length * breadth\n  SET perimeter = 2 * (length + breadth)\n  DISPLAY "Area = " + area\n  DISPLAY "Perimeter = " + perimeter\nEND`}</Code></Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 2 — Pseudocode (selection).</strong> Write pseudocode that reads a temperature in °C and displays: "Freezing" (below 0), "Cold" (0–15), "Warm" (16–25), "Hot" (above 25).</div>
          <Spoiler><Code>{`BEGIN\n  INPUT temp\n  IF temp < 0 THEN\n    DISPLAY "Freezing"\n  ELSE IF temp <= 15 THEN\n    DISPLAY "Cold"\n  ELSE IF temp <= 25 THEN\n    DISPLAY "Warm"\n  ELSE\n    DISPLAY "Hot"\n  END IF\nEND`}</Code></Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 3 — Flowchart.</strong> Draw a flowchart that asks the user for two numbers and prints the larger one.</div>
          <Spoiler>
            <div className="flex justify-center">
              <svg viewBox="0 0 500 480" className="max-w-xs w-full">
                <defs>
                  <marker id="arr2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#065f46"/>
                  </marker>
                </defs>
                <ellipse cx="250" cy="30" rx="55" ry="20" fill="#d1fae5" stroke="#065f46" strokeWidth="2"/>
                <text x="250" y="35" textAnchor="middle" fontSize="13" fill="#065f46" fontFamily="Georgia, serif">START</text>
                <line x1="250" y1="50" x2="250" y2="75" stroke="#065f46" strokeWidth="2" markerEnd="url(#arr2)"/>
                <polygon points="195,80 320,80 305,115 210,115" fill="#d1fae5" stroke="#065f46" strokeWidth="2"/>
                <text x="258" y="102" textAnchor="middle" fontSize="12" fill="#065f46" fontFamily="Georgia, serif">INPUT a, b</text>
                <line x1="258" y1="115" x2="258" y2="140" stroke="#065f46" strokeWidth="2" markerEnd="url(#arr2)"/>
                <polygon points="258,145 340,200 258,255 175,200" fill="#d1fae5" stroke="#065f46" strokeWidth="2"/>
                <text x="258" y="205" textAnchor="middle" fontSize="12" fill="#065f46" fontFamily="Georgia, serif">a &gt; b ?</text>
                <line x1="340" y1="200" x2="415" y2="200" stroke="#065f46" strokeWidth="2" markerEnd="url(#arr2)"/>
                <text x="350" y="193" fontSize="11" fill="#065f46" fontFamily="Georgia, serif">Yes</text>
                <polygon points="375,215 470,215 455,250 360,250" fill="#d1fae5" stroke="#065f46" strokeWidth="2"/>
                <text x="412" y="237" textAnchor="middle" fontSize="12" fill="#065f46" fontFamily="Georgia, serif">DISPLAY a</text>
                <line x1="258" y1="255" x2="258" y2="290" stroke="#065f46" strokeWidth="2" markerEnd="url(#arr2)"/>
                <text x="263" y="280" fontSize="11" fill="#065f46" fontFamily="Georgia, serif">No</text>
                <polygon points="200,295 315,295 300,330 215,330" fill="#d1fae5" stroke="#065f46" strokeWidth="2"/>
                <text x="258" y="317" textAnchor="middle" fontSize="12" fill="#065f46" fontFamily="Georgia, serif">DISPLAY b</text>
                <line x1="258" y1="330" x2="258" y2="430" stroke="#065f46" strokeWidth="2" markerEnd="url(#arr2)"/>
                <line x1="412" y1="250" x2="412" y2="400" stroke="#065f46" strokeWidth="2"/>
                <line x1="412" y1="400" x2="258" y2="400" stroke="#065f46" strokeWidth="2"/>
                <ellipse cx="258" cy="445" rx="45" ry="18" fill="#d1fae5" stroke="#065f46" strokeWidth="2"/>
                <text x="258" y="450" textAnchor="middle" fontSize="13" fill="#065f46" fontFamily="Georgia, serif">END</text>
              </svg>
            </div>
          </Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 4 — Java: random number.</strong> Write a Java statement that stores a random integer between 1 and 20 (inclusive) in a variable called <Inline>n</Inline>.</div>
          <Spoiler><Code>{`int n = (int)(Math.random() * 20) + 1;`}</Code></Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 5 — Java: if-else.</strong> A vet clinic charges R150 for a consultation. On a Saturday the fee is increased by 25%. Write a Java program that asks for the day of the week and prints the fee.</div>
          <Spoiler><Code>{`import java.util.Scanner;\n\npublic class VetFee {\n    public static void main(String[] args) {\n        Scanner input = new Scanner(System.in);\n        System.out.print("Enter the day: ");\n        String day = input.nextLine();\n        double fee = 150;\n        if (day.equals("Saturday")) {\n            fee = fee * 1.25;\n        }\n        System.out.println("Fee: R" + fee);\n    }\n}`}</Code></Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 6 — Java: for loop.</strong> Write a for loop that prints the multiplication table of 7 from 7×1 to 7×12.</div>
          <Spoiler><Code>{`for (int i = 1; i <= 12; i++) {\n    System.out.println("7 x " + i + " = " + (7 * i));\n}`}</Code></Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 7 — Java: while loop.</strong> Write a Java program that simulates rolling a dice until it rolls a 6, and prints how many rolls it took.</div>
          <Spoiler><Code>{`int roll = 0;\nint count = 0;\nwhile (roll != 6) {\n    roll = (int)(Math.random() * 6) + 1;\n    count = count + 1;\n    System.out.println("Rolled: " + roll);\n}\nSystem.out.println("It took " + count + " rolls.");`}</Code></Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 8 — Tracing.</strong> What does this code print?<Code>{`int x = 2;\nfor (int i = 1; i <= 4; i++) {\n    x = x * 2;\n}\nSystem.out.println(x);`}</Code></div>
          <Spoiler>
            <Table headers={["Iteration", "i", "x before", "x after"]} rows={[["1","1","2","4"],["2","2","4","8"],["3","3","8","16"],["4","4","16","32"]]} />
            <p>Output: <Inline>32</Inline></p>
          </Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm"><strong>Exercise 9 — Tracing a while loop.</strong> What does this print?<Code>{`int n = 15;\nwhile (n > 0) {\n    System.out.println(n);\n    n = n - 4;\n}`}</Code></div>
          <Spoiler><Code>{`15\n11\n7\n3`}</Code></Spoiler>

          <div className="p-3 my-3 rounded-md bg-amber-100 border border-amber-300 text-sm">
            <strong>Exercise 10 — Mini project.</strong> "PawCare" vet clinic runs a raffle where each customer picks a random number between 1 and 100. Numbers 1–10 win a free consultation, 11–30 get a 50% discount, anything else is no prize. Write a Java program that generates the random number and prints the customer's result.
          </div>
          <Spoiler><Code>{`public class PawCareRaffle {\n    public static void main(String[] args) {\n        int number = (int)(Math.random() * 100) + 1;\n        System.out.println("Your number is: " + number);\n        if (number >= 1 && number <= 10) {\n            System.out.println("You won a free consultation!");\n        } else if (number <= 30) {\n            System.out.println("You get a 50% discount.");\n        } else {\n            System.out.println("No prize this time.");\n        }\n    }\n}`}</Code></Spoiler>

          <InfoBox variant="tip" title="Done? What next?">
            <ul className="list-disc pl-5 space-y-1">
              <li>Re-do the exercises without looking at the answers.</li>
              <li>Drill the flashcards in quiz mode until nothing is in "still learning".</li>
              <li>Try to re-write each Java worked example from memory.</li>
            </ul>
          </InfoBox>
        </Section>

        <div className="mt-12 mb-6 text-center text-xs text-amber-700 border-t border-amber-900/20 pt-6">
          <div style={{ fontFamily: "Palatino, serif" }} className="text-sm italic">Solution Development — A Grade 10 Textbook</div>
          <div className="mt-1">Helpmekaar Kollege · IT · 2026</div>
        </div>

        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-6 right-6 p-3 rounded-full bg-amber-900 text-amber-50 shadow-lg hover:bg-amber-950 transition" title="Back to top">
          <ArrowUp className="w-5 h-5" />
        </button>

      </main>
    </div>
  );
}
